import { useCallback, useState } from 'react';
import { API_BASE_URL } from '../../config';

const ACTIVE = new Set(['running', 'queued', 'pending', 'cancelling']);

function buildFieldsPayload(fieldsData) {
  const standard = fieldsData?.standard_fields || {};
  const entries = Object.entries(standard).map(([key, info]) => ({
    field_key: key,
    field_name: info?.name || key,
    is_standard_field: true,
    label: info?.label || key,
  }));
  const withGaps = entries.filter((field) => {
    const stats = standard[field.field_key] || {};
    return Number(stats.empty_count || 0) > 0 || Number(stats.unique_values || 0) > 1;
  });
  const chosen = (withGaps.length ? withGaps : entries).slice(0, 12);
  return chosen.map((field) => ({
    field_key: field.field_key,
    field_name: field.field_name,
    is_standard_field: field.is_standard_field,
    field_label: field.label,
  }));
}

export function usePipelineActions({ getAuthHeaders, onSuccess, onError }) {
  const [startingId, setStartingId] = useState(null);

  const startStep = useCallback(
    async (step, options = {}) => {
      const stepId = step?.id;
      if (!stepId || startingId) return;
      if (!step.can_start) {
        onError?.(step.start_hint || 'This step cannot be started from the dashboard.');
        return;
      }
      if (ACTIVE.has(String(step.status || '').toLowerCase())) {
        onError?.('A job is already running for this step.');
        return;
      }

      const headers = getAuthHeaders();
      setStartingId(stepId);
      try {
        let url;
        let body = {};

        switch (stepId) {
          case 'ahri_data_sync':
            url = `${API_BASE_URL}/api/v1/ahri/sync/start`;
            body = { programs: options.programs || [] };
            break;
          case 'product_data_sync': {
            // Product data sync always goes through Documents v4 with a saved
            // column mapping. The Start button is disabled by the backend
            // until both a data source and a v4 mapping preset are saved.
            url = `${API_BASE_URL}/api/v1/ahri/dashboard/run/${stepId}`;
            body = { options: options.runOptions || {} };
            break;
          }
          case 'inventory_sync': {
            url = `${API_BASE_URL}/api/v1/ahri/dashboard/run/${stepId}`;
            body = { options: options.runOptions || {} };
            break;
          }
          case 'product_mapping':
            url = `${API_BASE_URL}/api/v1/ahri/product-mapping-v2/start`;
            body = {};
            break;
          case 'attribute_mapping':
            url = `${API_BASE_URL}/api/v1/ahri/product-attributes-sync/start`;
            body = { sync_mode: 'replace', mapped_only: true };
            break;
          case 'data_quality': {
            const fieldsResp = await fetch(`${API_BASE_URL}/api/v1/data-quality/fields`, { headers });
            const fieldsData = await fieldsResp.json().catch(() => ({}));
            if (!fieldsResp.ok) {
              throw new Error(fieldsData.detail || 'Unable to load analyzable fields');
            }
            const fields = buildFieldsPayload(fieldsData);
            if (!fields.length) {
              throw new Error('No analyzable fields found. Open Data Quality to configure a run.');
            }
            url = `${API_BASE_URL}/api/v1/data-quality/analysis-jobs/start`;
            body = { fields };
            break;
          }
          default:
            throw new Error('Unknown pipeline step');
        }

        const resp = await fetch(url, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          throw new Error(data.detail || data.message || `Start failed (${resp.status})`);
        }
        onSuccess?.(step, data);
      } catch (err) {
        onError?.(err.message || 'Unable to start process');
      } finally {
        setStartingId(null);
      }
    },
    [getAuthHeaders, onError, onSuccess, startingId],
  );

  return { startingId, startStep, isStarting: Boolean(startingId) };
}

export default usePipelineActions;
